/** Liste e criteri: criteri di severità, voci delle liste (cause, società, funzioni, recupero, verifica), cambio USD → EUR. */
import { App, Button, Card, Col, Input, InputNumber, Row, Space, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { LISTE, SEVERITA, type CriterioSeveritaDto, type Lista, type VoceListaDto } from "@qa/shared";
import { api } from "../../api/client";
import { useAggiorna } from "../../lib/query";
import { locale } from "../../i18n";
import { messaggioErrore } from "../../i18n/errori";
import { useListe } from "../../components/campi";
import { PageHead, Sev, Stato } from "../../components/ui";

function useScrivi() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const aggiorna = useAggiorna();
  return (ok: string) => ({
    onSuccess: () => {
      message.success(ok);
      void qc.invalidateQueries({ queryKey: ["liste"] });
      void aggiorna();
    },
    onError: (e: unknown) => message.error(messaggioErrore(e)),
  });
}

function Criteri({ criteri }: { criteri: CriterioSeveritaDto[] }) {
  const { t } = useTranslation();
  const scrivi = useScrivi();
  const da = () => Object.fromEntries(SEVERITA.map((s) => [s, criteri.find((c) => c.severita === s)?.testo ?? ""]));
  const [v, setV] = useState<Record<string, string>>(da);
  useEffect(() => setV(da()), [criteri]); // eslint-disable-line react-hooks/exhaustive-deps
  const m = useMutation({ mutationFn: () => api.put("/admin/criteri", { criteri: SEVERITA.filter((s) => v[s]?.trim()).map((s) => ({ severita: s, testo: v[s].trim() })) }), ...scrivi(t("admin.liste.criteriSalvati")) });
  return (
    <Card size="small" title={t("admin.liste.severita")} style={{ height: "100%" }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        {SEVERITA.map((s) => (
          <div key={s} style={{ display: "grid", gridTemplateColumns: "100px 1fr", gap: 8, alignItems: "start" }}>
            <Sev s={s} />
            <Input.TextArea autoSize value={v[s]} maxLength={500} onChange={(e) => setV({ ...v, [s]: e.target.value })} />
          </div>
        ))}
        <Button type="primary" size="small" loading={m.isPending} onClick={() => m.mutate()}>
          {t("admin.liste.salvaCriteri")}
        </Button>
      </Space>
    </Card>
  );
}

function ListaCard({ lista, voci }: { lista: Lista; voci: VoceListaDto[] }) {
  const { t } = useTranslation();
  const scrivi = useScrivi();
  const [nuova, setNuova] = useState("");
  const crea = useMutation({ mutationFn: () => api.post("/admin/liste", { lista, valore: nuova.trim() }), ...scrivi(t("admin.liste.aggiunta")), onSettled: () => setNuova("") });
  const cambia = useMutation({ mutationFn: (a: { id: string; attiva: boolean }) => api.patch(`/admin/liste/${a.id}`, { attiva: a.attiva }), ...scrivi(t("comune.salvato")) });
  return (
    <Card size="small" title={t(`dominio.lista.${lista}`)} style={{ height: "100%" }}>
      <Table
        size="small"
        rowKey="id"
        showHeader={false}
        pagination={false}
        dataSource={[...voci].sort((a, b) => a.ordine - b.ordine)}
        columns={[
          { key: "v", render: (_, x) => <span style={x.attiva ? undefined : { textDecoration: "line-through", opacity: 0.6 }}>{x.valore}</span> },
          {
            key: "a",
            width: 120,
            align: "right",
            render: (_, x) => (
              <>
                {!x.attiva && <Tag>{t("admin.liste.disattivata")}</Tag>}
                <Button size="small" type="link" onClick={() => cambia.mutate({ id: x.id, attiva: !x.attiva })}>
                  {x.attiva ? t("admin.liste.disattiva") : t("admin.liste.riattiva")}
                </Button>
              </>
            ),
          },
        ]}
      />
      <Space.Compact style={{ width: "100%", marginTop: 8 }}>
        <Input value={nuova} maxLength={100} placeholder={t("admin.liste.nuovaVoce")} onChange={(e) => setNuova(e.target.value)} onPressEnter={() => nuova.trim() && crea.mutate()} />
        <Button onClick={() => crea.mutate()} disabled={!nuova.trim()} loading={crea.isPending}>
          {t("azioni.aggiungi")}
        </Button>
      </Space.Compact>
    </Card>
  );
}

function Cambio({ valore }: { valore: number }) {
  const { t } = useTranslation();
  const scrivi = useScrivi();
  const [v, setV] = useState<number | null>(valore);
  useEffect(() => setV(valore), [valore]);
  const m = useMutation({ mutationFn: () => api.put("/admin/cambio", { cambioUsdEur: v }), ...scrivi(t("admin.liste.cambioSalvato")) });
  return (
    <Card size="small" title={t("admin.liste.cambio")} style={{ height: "100%" }}>
      <Space>
        <InputNumber min={0.01} max={9.99} step={0.01} value={v} onChange={(x) => setV(x)} decimalSeparator={locale() === "it-IT" ? "," : "."} className="mono" style={{ width: 120 }} />
        <Button type="primary" size="small" disabled={!v || v === valore} loading={m.isPending} onClick={() => m.mutate()}>
          {t("azioni.salva")}
        </Button>
      </Space>
      <p className="muted" style={{ margin: "8px 0 0" }}>
        {t("admin.liste.cambioNota")}
      </p>
    </Card>
  );
}

export default function ListePage() {
  const { t } = useTranslation();
  const q = useListe();
  return (
    <>
      <PageHead crumbs={[{ label: t("portale.nome"), to: "/" }, { label: t("menu.gruppoAdmin") }, { label: t("menu.liste") }]} title={t("admin.liste.titolo")} sub={t("admin.liste.sottotitolo")} />
      <Stato q={q}>
        {(l) => (
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Criteri criteri={l.criteri} />
            </Col>
            <Col xs={24} lg={12}>
              <Cambio valore={l.cambioUsdEur} />
            </Col>
            {LISTE.map((x) => (
              <Col xs={24} lg={12} key={x}>
                <ListaCard lista={x} voci={l.voci.filter((v) => v.lista === x)} />
              </Col>
            ))}
          </Row>
        )}
      </Stato>
    </>
  );
}
